<template>
  <ion-page>
    <ion-content class="welcome-content">
      <div class="background-scene">
        <div class="stars"></div>
        
        <!-- Mathematical Equations Background -->
        <div class="math-equations">
          <div class="equation equation-1">∫ f(x)dx = F(x) + C</div>
          <div class="equation equation-2">e^(iπ) + 1 = 0</div>
          <div class="equation equation-3">∇ × E = -∂B/∂t</div>
          <div class="equation equation-4">∑(n=1→∞) 1/n² = π²/6</div>
          <div class="equation equation-5">φ = (1 + √5)/2</div>
          <div class="equation equation-6">∂²u/∂t² = c²∇²u</div>
          <div class="equation equation-7">∫₀^∞ e^(-x²)dx = √π/2</div>
          <div class="equation equation-8">ζ(s) = ∑(n=1→∞) 1/n^s</div>
          <div class="equation equation-9">∇ · E = ρ/ε₀</div>
          <div class="equation equation-10">Γ(n) = (n-1)!</div>
        </div>
      </div>
      
      <div class="register-container">

        <div class="register-header">
          <h2 class="register-title">Create Account</h2>
        </div>

        <!-- Step 1: Plan Selection -->
        <div v-if="!planSelected" class="plan-selection-step">
          <div class="login-link">
                Already have an account? 
                <router-link to="/login" aria-label="Sign in">Sign in</router-link>
              </div>
          <div class="plans-container">
            <div 
              class="plan-card" 
              :class="{ active: selectedPlan === 'basic' }"
              @click="selectedPlan = 'basic'"
            >
              <div class="plan-card-header">
                <h3 class="plan-title">Basic Plan</h3>
                <div class="plan-price">
                  <span class="price-amount">$4.99</span>
                  <span class="price-period">/month</span>
                </div>
              </div>
              <div class="plan-features">
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Up to 1 profile</span>
                </div>
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Basic calendar features</span>
                </div>
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Standard support</span>
                </div>
              </div>
              <div class="plan-card-footer">
                <!-- Empty footer for Basic Plan -->
              </div>
            </div>

            <div 
              class="plan-card premium" 
              :class="{ active: selectedPlan === 'premium' }"
              @click="selectedPlan = 'premium'"
            >
              <div class="plan-card-header">
                <h3 class="plan-title">Premium Plan</h3>
                <div class="plan-price">
                  <span class="price-amount">$9.99</span>
                  <span class="price-period">/month</span>
                </div>
              </div>
              <div class="plan-features">
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Unlimited profiles</span>
                </div>
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Advanced calendar features</span>
                </div>
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Priority support</span>
                </div>
                <div class="feature-item">
                  <ion-icon :icon="checkmarkOutline" class="feature-icon"></ion-icon>
                  <span>Advanced analytics</span>
                </div>
              </div>
              <div class="plan-card-footer">
                <div class="premium-badge">Most Popular</div>
              </div>
            </div>
          </div>

          <div class="plan-selection-actions">
            <ion-button 
              @click="selectPlan" 
              expand="block" 
              class="continue-button"
              :disabled="!selectedPlan"
              aria-label="Continue with selected plan"
            >
              Continue with {{ selectedPlan === 'basic' ? 'Basic' : 'Premium' }} Plan
            </ion-button>
          </div>
        </div>

        <!-- Step 2: Registration Form -->
        <div v-else class="registration-form-step">
          <div class="step-indicator">
            <div class="step-item completed" @click="changePlan" style="cursor: pointer;">
              <div class="step-number">1</div>
              <span class="step-label">Choose Plan</span>
            </div>
            <div class="step-connector"></div>
            <div class="step-item active">
              <div class="step-number">2</div>
              <span class="step-label">Create Account</span>
            </div>
          </div>

          <form @submit.prevent="handleRegister" class="register-form">
            <div class="form-fields-stack">
              <!-- Form Fields -->
              <div class="form-row animate-slide-up" style="animation-delay: 0.1s;">
                <div class="register-input-group">
                  <ion-label>First Name</ion-label>
                  <input 
                    v-model="firstname" 
                    type="text" 
                    placeholder="Enter first name"
                    class="register-custom-input"
                    required
                    aria-label="First Name"
                  />
                </div>
                
                <div class="register-input-group">
                  <ion-label>Last Name</ion-label>
                  <input 
                    v-model="lastname" 
                    type="text" 
                    placeholder="Enter last name"
                    class="register-custom-input"
                    required
                    aria-label="Last Name"
                  />
                </div>
              </div>

              <div class="form-row animate-slide-up" style="animation-delay: 0.2s;">
                <div class="register-input-group">
                  <ion-label>Username</ion-label>
                  <input 
                    v-model="username" 
                    type="text" 
                    placeholder="Choose username"
                    class="register-custom-input"
                    required
                    aria-label="Username"
                  />
                </div>

                <div class="register-input-group">
                  <ion-label>Email</ion-label>
                  <input 
                    v-model="email" 
                    type="email" 
                    placeholder="Enter email"
                    class="register-custom-input"
                    required
                    aria-label="Email"
                  />
                </div>
              </div>

              <div class="form-row animate-slide-up" style="animation-delay: 0.3s;">
                <div class="register-input-group">
                  <ion-label>Phone Number</ion-label>
                  <input 
                    v-model="phone" 
                    type="tel"
                    placeholder="Enter phone number"
                    class="register-custom-input"
                    required
                    aria-label="Phone Number"
                  />
                </div>

                <div class="register-input-group">
                  <ion-label>Password</ion-label>
                  <input 
                    v-model="password" 
                    type="password" 
                    placeholder="Enter password"
                    class="register-custom-input"
                    required
                    aria-label="Password"
                  />
                </div>
              </div>

              <div class="register-input-group animate-slide-up" style="animation-delay: 0.4s;">
                <ion-label>Confirm Password</ion-label>
                <input 
                  v-model="confirmPassword" 
                  type="password" 
                  placeholder="Confirm password"
                  class="register-custom-input"
                  required
                  aria-label="Confirm Password"
                />
              </div>
            </div>

            <div class="register-form-actions animate-slide-up" style="animation-delay: 0.5s;">
              <ion-button 
                type="submit" 
                expand="block" 
                class="register-button"
                :class="{ 'loading': isLoading }"
                :disabled="isLoading"
                aria-label="Create Account"
              >
                <div v-if="isLoading" class="loading-spinner">
                  <ion-spinner name="crescent"></ion-spinner>
                </div>
                <span v-else>Create Account</span>
              </ion-button>
            </div>
          </form>
        </div>
      </div>

      <!-- Toast Notification -->
      <div v-if="showToast" class="toast-notification" :class="toastType">
        <div class="toast-content">
          <ion-icon :icon="toastIcon" class="toast-icon"></ion-icon>
          <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <ion-button fill="clear" class="toast-close" @click="hideToast">
          <ion-icon :icon="close"></ion-icon>
        </ion-button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonContent, IonInput, IonButton, IonIcon, IonLabel, IonSpinner } from '@ionic/vue';
import { checkmarkOutline, close, checkmark, alertCircle } from 'ionicons/icons';
import axios from 'axios';
import {getAPIURL} from '../logic/api';

export default {
  name: 'RegisterPage',
  components: {
    IonPage,
    IonContent,
    IonInput,
    IonButton,
    IonIcon,
    IonLabel,
    IonSpinner
  },
  data() {
    return {
      firstname: '',
      lastname: '',
      username: '',
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
      checkmarkOutline,
      close,
      checkmark,
      alertCircle,
      selectedPlan: 'basic',
      planSelected: false,
      isLoading: false,
      showToast: false,
      toastMessage: '',
      toastType: 'error',
      toastIcon: alertCircle
    }
  },
  methods: {
    selectPlan() {
      if (this.selectedPlan) {
        this.planSelected = true;
      }
    },
    
    changePlan() {
      this.planSelected = false;
    },
    
    showSuccessToast(message) {
      this.toastMessage = message;
      this.toastType = 'success';
      this.toastIcon = this.checkmark;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 4000);
    },
    showErrorToast(message) {
      this.toastMessage = message;
      this.toastType = 'error';
      this.toastIcon = this.alertCircle;
      this.showToast = true;
      setTimeout(() => {
        this.hideToast();
      }, 5000);
    },
    hideToast() {
      this.showToast = false;
    },
    
    async handleRegister() {
      if (this.password !== this.confirmPassword) {
        this.showErrorToast('Password needs to match confirm password');
        return;
      }
      
      this.isLoading = true;
      
      try {
        this.showErrorToast('Registration is currently disabled');
      } catch (error) {
        console.error('Registration failed:', error);
        this.showErrorToast('Registration failed: ' + (error.response?.statusText || 'Unknown error'));
      } finally {
        this.isLoading = false;
      }
    },

    async login() {
      try {
        const response = await axios.post(getAPIURL()+'/api/user/login', {
          username: this.username,
          password: this.password
        });
        
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userID', response.data.userID);
        this.$router.push('/dashboard');
      } catch (error) {
        console.error('Login failed:', error);
        this.showErrorToast('Account created but login failed. Please try logging in manually.');
      }
    }
  }
}
</script>

<style scoped>
/* RegisterPage-specific styles only */

/* Top Navigation */
.top-navigation {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.login-nav-button {
  --color: rgba(255, 255, 255, 0.8);
  --color-hover: white;
  font-size: 0.9rem;
  text-transform: none;
  font-weight: 500;
  --padding-start: 12px;
  --padding-end: 12px;
  height: 36px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.login-nav-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* Selection Indicator */
.selection-indicator {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.selection-indicator.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.plan-card.premium .selection-indicator.active {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 15px rgba(240, 147, 251, 0.4);
}

.selection-indicator ion-icon {
  color: white;
  font-size: 1rem;
}

/* Loading Spinner */
.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner ion-spinner {
  --color: white;
  width: 20px;
  height: 20px;
}

/* Toast Notification Styles */
.toast-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 300px;
  max-width: 400px;
  animation: slideInRight 0.3s ease-out;
}

.toast-notification.success {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.9) 0%, rgba(56, 142, 60, 0.9) 100%);
  border-color: rgba(76, 175, 80, 0.3);
}

.toast-notification.error {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.9) 0%, rgba(211, 47, 47, 0.9) 100%);
  border-color: rgba(244, 67, 54, 0.3);
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.toast-icon {
  font-size: 1.2rem;
  color: white;
  min-width: 20px;
}

.toast-message {
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
}

.toast-close {
  --color: white;
  --color-hover: rgba(255, 255, 255, 0.8);
  margin-left: 12px;
  min-width: 24px;
  height: 24px;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive adjustments for toast */
@media (max-width: 768px) {
  .toast-notification {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
}
</style>

<style>
/* Light Theme Styles for Register Page */
body.light-theme .background-scene {
  background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

body.light-theme .stars {
  opacity: 0 !important;
}

body.light-theme .register-title {
  background: linear-gradient(135deg, #6f3cc4 0%, #5a2da3 25%, #4a1d8a 50%, #3a1470 75%, #2a0f56 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
}

body.light-theme .register-input-group ion-label {
  color: #333 !important;
}

body.light-theme .register-custom-input {
  background: #fff !important;
  background-color: #fff !important;
  border: 2px solid #ddd !important;
  border-color: #ddd !important;
  color: #333 !important;
  --border-color: #ddd !important;
}

body.light-theme .register-custom-input::placeholder {
  color: #999 !important;
}

body.light-theme .register-custom-input:focus {
  border-color: #6f3cc4 !important;
  border: 2px solid #6f3cc4 !important;
  background: #fff !important;
  background-color: #fff !important;
  --border-color: #6f3cc4 !important;
  box-shadow: 0 8px 25px rgba(111, 60, 196, 0.15) !important;
}

body.light-theme .register-custom-input:focus::placeholder {
  color: #666 !important;
}

/* Light theme plan cards */
body.light-theme .plan-card {
  background: #fff !important;
  border: 2px solid #ddd !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
}

body.light-theme .plan-card:hover {
  border-color: #6f3cc4 !important;
  box-shadow: 0 20px 40px rgba(111, 60, 196, 0.15) !important;
}

body.light-theme .plan-card.active {
  border-color: #6f3cc4 !important;
  background: #f0e6ff !important;
  box-shadow: 0 20px 40px rgba(111, 60, 196, 0.2) !important;
}

body.light-theme .plan-card.premium {
  border-color: #f093fb !important;
}

body.light-theme .plan-card.premium:hover {
  border-color: #f093fb !important;
  box-shadow: 0 20px 40px rgba(240, 147, 251, 0.15) !important;
}

body.light-theme .plan-card.premium.active {
  border-color: #f093fb !important;
  background: #fef0ff !important;
  box-shadow: 0 20px 40px rgba(240, 147, 251, 0.2) !important;
}

body.light-theme .plan-title {
  color: #333 !important;
}

body.light-theme .price-amount {
  background: linear-gradient(135deg, #6f3cc4 0%, #5a2da3 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
}

body.light-theme .plan-card.premium .price-amount {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
}

body.light-theme .price-period {
  color: #666 !important;
}

body.light-theme .feature-item {
  color: #333 !important;
}

body.light-theme .feature-icon {
  color: #34c759 !important;
}

/* Light theme step indicators */
body.light-theme .step-label {
  color: #333 !important;
}

body.light-theme .step-item.completed .step-label,
body.light-theme .step-item.active .step-label {
  color: #333 !important;
  opacity: 1 !important;
}

body.light-theme .step-connector {
  background: linear-gradient(90deg, #34c759 0%, #6f3cc4 100%) !important;
}

/* Light theme buttons */
body.light-theme .continue-button {
  --background: #6f3cc4 !important;
  --background-hover: #5a2da3 !important;
  --color: white !important;
  box-shadow: 0 4px 15px rgba(111, 60, 196, 0.3) !important;
}

body.light-theme .continue-button:not(.ion-disabled):hover {
  box-shadow: 0 8px 25px rgba(111, 60, 196, 0.4) !important;
}

body.light-theme .continue-button.ion-disabled {
  --background: #f5f5f5 !important;
  --color: #999 !important;
}

body.light-theme .register-button {
  --background: #6f3cc4 !important;
  --background-hover: #5a2da3 !important;
  --color: white !important;
  box-shadow: 0 4px 15px rgba(111, 60, 196, 0.3) !important;
}

body.light-theme .register-button:not(.loading):hover {
  box-shadow: 0 8px 25px rgba(111, 60, 196, 0.4) !important;
}

body.light-theme .login-link {
  color: #555 !important;
}

body.light-theme .login-link a {
  color: #6f3cc4 !important;
}

body.light-theme .login-link a:hover {
  color: #5a2da3 !important;
}

/* Light theme toast adjustments */
body.light-theme .toast-notification {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

body.light-theme .toast-notification.success {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.95) 0%, rgba(56, 142, 60, 0.95) 100%) !important;
  border-color: rgba(76, 175, 80, 0.3) !important;
}

body.light-theme .toast-notification.error {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.95) 0%, rgba(211, 47, 47, 0.95) 100%) !important;
  border-color: rgba(244, 67, 54, 0.3) !important;
}
</style> 